import { ensureDOMInstance } from '@hzdg/dom-utils';
import Observable from 'zen-observable';

export interface ResizeObservableSize extends DOMRectReadOnly {
  readonly contentBoxSize: ResizeObserverSize;
  readonly borderBoxSize: ResizeObserverSize;
}

type Observer = ZenObservable.SubscriptionObserver<ResizeObservableSize>;

const resizeObservers = new Map<Element, Set<Observer>>();
const latestSize = new Map<Element, ResizeObservableSize>();
let resizeObserver: ResizeObserver;

function createPayload(entry: ResizeObserverEntry): ResizeObservableSize {
  const { contentBoxSize, borderBoxSize, contentRect } = entry;
  const payload = {
    contentBoxSize: contentBoxSize
      ? contentBoxSize[0]
      : { blockSize: contentRect.height + 32, inlineSize: contentRect.width },
    borderBoxSize: borderBoxSize
      ? borderBoxSize[0]
      : { blockSize: contentRect.height + 32, inlineSize: contentRect.width },
    ...contentRect.toJSON(),
  };
  const frozenPayload = Object.freeze({
    ...payload,
    toJSON() {
      return JSON.parse(
        JSON.stringify(this === frozenPayload ? payload : this),
      );
    },
  });
  return frozenPayload;
}

function createResizeObserver(): void {
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { target: element } = entry;
      const observers = resizeObservers.get(element);
      if (observers) {
        const payload = createPayload(entry);
        latestSize.set(element, payload);
        observers.forEach((observer) => observer.next(payload));
      }
    }
  });
}

export function create(element: Element): Observable<ResizeObservableSize> {
  ensureDOMInstance(element, Element);
  return new Observable((observer: Observer) => {
    if (!resizeObserver) createResizeObserver();
    let observers = resizeObservers.get(element);
    if (observers) {
      observers.add(observer);
      const size = latestSize.get(element);
      if (size) observer.next(size);
    } else {
      observers = new Set<Observer>([observer]);
      resizeObservers.set(element, observers);
      resizeObserver.observe(element);
    }
    return () => {
      if (observers) {
        observers.delete(observer);
        if (observers.size === 0) {
          latestSize.delete(element);
          resizeObservers.delete(element);
          resizeObserver.unobserve(element);
        }
      }
    };
  });
}

export default { create };
